import { useNavigate } from "react-router-dom";
import { ArrowRight, Plus } from "lucide-react";
import { GoldBase, Shelf } from "@/components/kit/Organizer";
import { useElementWidth, useKitQuery } from "@/components/kit/useKit";

const chunk = (arr, n) => Array.from({ length: Math.ceil(arr.length / n) }, (_, i) => arr.slice(i * n, i * n + n));

export function kitPreview(kit, width) {
  const perRow = width && width < 300 ? 3 : 4;
  const capacity = perRow * 2;
  const shown = kit.slice(0, capacity);
  return { perRow, shown, rows: chunk(shown, perRow), more: kit.length - shown.length };
}

function EmptyOrganizer() {
  return (
    <div className="relative" aria-hidden="true">
      <div className="relative h-20 rounded-[10px] border border-white/80 bg-gradient-to-b from-white/50 to-white/70 shadow-[inset_0_1px_0_rgba(255,255,255,.9)]">
        <div className="absolute inset-0 grid grid-cols-4">{[0, 1, 2, 3].map((i) => <span key={i} className={i ? "border-l border-white/80" : ""} />)}</div>
      </div>
    </div>
  );
}

export function KitCard() {
  const nav = useNavigate();
  const { data: kit = [], isLoading: loading, isError, refetch, isFetching } = useKitQuery();
  const isLoading = loading || isError;
  const [ref, width] = useElementWidth();
  const { perRow, shown, rows, more } = kitPreview(kit, width);
  const total = kit.length;
  const open = () => nav("/kit");
  return (
    <div data-testid="dashboard-kit-card" className="ig-rise ig-d3 flex flex-col rounded-3xl border border-[#EFE6DA] bg-[#FBF7F1] p-6 shadow-[0_18px_50px_rgba(44,24,16,0.06)] lg:col-span-5">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-serif text-2xl font-semibold text-[#2C1810] sm:text-[28px]">My Pigment Kit</h3>
        <span className="shrink-0 rounded-full border border-[#E3D3B5] bg-[#F7EEDD] px-3.5 py-1 text-sm text-[#6B5434]" data-testid="dashboard-kit-count">
          {isLoading ? "…" : `${total} pigment${total === 1 ? "" : "s"}`}
        </span>
      </div>
      <div ref={ref} className="mt-4 flex h-[330px] flex-col justify-end">
        {isError ? (
          <div className="flex h-full flex-col items-center justify-center rounded-2xl border border-dashed border-[#E8BDB5] bg-[#FDF2F0] p-6 text-center" data-testid="dashboard-kit-error">
            <p className="font-serif text-xl text-[#2C1810]">We couldn't load your kit</p>
            <p className="mt-1 text-sm text-[#6E5549]">Your saved pigments are safe. Please try again.</p>
            <button onClick={() => refetch()} disabled={isFetching} data-testid="dashboard-kit-retry" className="mt-4 rounded-full bg-[#2C1810] px-5 py-2.5 text-sm font-semibold text-[#FDFBF7] disabled:opacity-60">{isFetching ? "Retrying…" : "Retry"}</button>
          </div>
        ) : total === 0 && !isLoading ? (
          <div className="flex h-full flex-col justify-end" data-testid="dashboard-kit-empty">
            <div className="flex flex-1 flex-col items-center justify-center text-center">
              <p className="font-serif text-2xl text-[#2C1810]">Your kit is empty</p>
              <p className="mt-1 max-w-[16rem] text-sm text-[#6E5549]">Save the pigments you own and Ink Genius will look for kit-first options.</p>
            </div>
            <EmptyOrganizer />
            <GoldBase />
          </div>
        ) : (
          <button type="button" onClick={open} className="group block w-full text-left" data-testid="dashboard-kit-organizer" aria-label={`Open my kit, ${total} pigments`}>
            <div className="space-y-1 transition-transform duration-300 group-hover:-translate-y-0.5">
              {rows.map((r, i) => <Shelf key={r[0].id} items={r} perRow={perRow} compact bottleMax={86} rim={false} testId={`dashboard-kit-shelf-${i}`} />)}
            </div>
            <GoldBase />
          </button>
        )}
      </div>
      <div className="mt-3 flex h-6 items-center justify-center">
        {more > 0 && (
          <button onClick={open} className="text-[15px] text-[#9C7A43] underline decoration-[#C5A059]/60 underline-offset-4 hover:text-[#2C1810]" data-testid="dashboard-kit-more">
            + {more} more in your kit
          </button>
        )}
      </div>
      <div className="mt-3 flex min-h-[76px] flex-wrap content-start items-center gap-x-5 gap-y-2 sm:min-h-12">
        {total === 0 && !isLoading ? (
          <>
            <button onClick={() => nav("/kit?add=1")} data-testid="dashboard-kit-add-first" className="inline-flex h-12 items-center gap-2 rounded-full bg-[#C5A059] px-6 text-sm font-semibold text-[#2C1810] transition-colors hover:bg-[#d4b273]">
              <Plus size={16} /> Add your first pigment
            </button>
            <button onClick={open} data-testid="dashboard-kit-open" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4A3B32] hover:text-[#2C1810]">
              Open my kit <ArrowRight size={15} />
            </button>
          </>
        ) : (
          <>
            <button onClick={open} data-testid="dashboard-kit-open" className="inline-flex h-12 items-center gap-2 rounded-full bg-[#C5A059] px-6 text-sm font-semibold text-[#2C1810] transition-colors hover:bg-[#d4b273]">
              Open my kit <ArrowRight size={16} />
            </button>
            <button onClick={() => nav("/kit?add=1")} data-testid="dashboard-kit-add" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4A3B32] hover:text-[#2C1810]">
              <Plus size={15} /> Add pigment
            </button>
          </>
        )}
      </div>
      <p className="mt-3 h-4 text-xs text-[#A88B7D]" data-testid="dashboard-kit-showing">{total > 0 && !isError ? `Showing ${shown.length} of ${total} pigment${total === 1 ? "" : "s"}` : ""}</p>
    </div>
  );
}
